import Tooltip from "@mui/material/Tooltip";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import debounce from "lodash/debounce";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import * as _screenfull from "screenfull";
import { useTranslation } from "react-i18next";
import { Button } from "./Button";
import styles from "./ScaleContainer.module.css";

const screenfull = _screenfull as _screenfull.Screenfull;

interface Props {
  /** Padding from the sides  */
  padding?: number;
  scale: number;
  onScaleUpdate: (scale: number) => void;
}

export const ScaleContainer: FunctionComponent<Props> = ({
  children,
  scale,
  onScaleUpdate,
  padding = 60,
}) => {
  const { t } = useTranslation();
  const TooltipAny: any = Tooltip;
  const containerRef = useRef<HTMLDivElement>();
  const contentRef = useRef<HTMLDivElement>();
  const [isFullscreen, setIsFullscreen] = useState(false);

  const calculateScale = debounce(() => {
    if (!containerRef.current || !contentRef.current) return;

    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;
    const contentWidth = contentRef.current.clientWidth;
    const contentHeight = contentRef.current.clientHeight;

    const scaleX = (containerWidth - padding) / contentWidth;
    const scaleY = (containerHeight - padding) / contentHeight;

    // Clamp scale between 0.01 and 1 to avoid zero/negative values
    const computed = Math.min(
      parseFloat(scaleX.toFixed(2)),
      parseFloat(scaleY.toFixed(2)),
      1
    );
    const clamped = Math.max(computed, 0.01);
    onScaleUpdate(clamped);

    setIsFullscreen(!!document.fullscreenElement);
  }, 200);

  useEffect(() => {
    window.addEventListener("resize", calculateScale);
    screenfull.isEnabled && screenfull.on("change", calculateScale);

    return () => {
      window.removeEventListener("resize", calculateScale);
      screenfull.isEnabled && screenfull.off("change", calculateScale);
    };
  }, [containerRef, contentRef]);

  useEffect(() => {
    if (containerRef.current && contentRef.current) calculateScale();
    // Recalculate after first paint to ensure layout is measured
    const id = requestAnimationFrame(() => calculateScale());
    return () => cancelAnimationFrame(id);
  }, [containerRef, contentRef, children]);

  return (
    <div ref={containerRef} className={styles.container}>
      {screenfull.isEnabled && (
        <Button
          className={styles.fullscreenButton}
          size="small"
          tertiary
          onClick={() => {
            if (screenfull.isFullscreen) {
              screenfull.exit();
            } else {
              screenfull.request(containerRef.current);
            }
          }}
        >
          <TooltipAny
            title={t("ui_toggle-fullscreen")}
            PopperProps={{ disablePortal: true }}
            placement="left"
          >
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </TooltipAny>
        </Button>
      )}
      <div
        ref={contentRef}
        className={styles.content}
        style={{ transform: `translate(-50%, -50%) scale(${scale})` }}
      >
        {children}
      </div>
    </div>
  );
};
